import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@payment-plan/ui';
import { advisorSchema, type AdvisorFormValues } from './advisor.schema';
import { useCreateAdvisor } from './use-advisors';
import type { Project } from '../../../types/admin';

interface AdvisorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proyectos: Project[];
}

const EMPTY: AdvisorFormValues = {
  nombre: '',
  documento: '',
  correo: '',
  celular: '',
  proyectoIds: [],
};

export function AdvisorFormDialog({
  open,
  onOpenChange,
  proyectos,
}: AdvisorFormDialogProps): React.JSX.Element {
  const createAdvisor = useCreateAdvisor();
  const [serverError, setServerError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AdvisorFormValues>({
    resolver: zodResolver(advisorSchema),
    defaultValues: EMPTY,
  });

  React.useEffect(() => {
    if (open) {
      setServerError(null);
      reset(EMPTY);
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await createAdvisor.mutateAsync(values);
      onOpenChange(false);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'No fue posible crear el asesor.',
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Nuevo asesor</DialogTitle>
          <DialogDescription>
            El asesor recibirá un correo con un enlace para definir su contraseña.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormField label="Nombre" htmlFor="advisor-nombre" required error={errors.nombre?.message}>
            <Input id="advisor-nombre" error={Boolean(errors.nombre)} {...register('nombre')} />
          </FormField>
          <FormField
            label="Documento"
            htmlFor="advisor-documento"
            required
            error={errors.documento?.message}
          >
            <Input
              id="advisor-documento"
              error={Boolean(errors.documento)}
              {...register('documento')}
            />
          </FormField>
          <FormField label="Correo" htmlFor="advisor-correo" required error={errors.correo?.message}>
            <Input
              id="advisor-correo"
              type="email"
              error={Boolean(errors.correo)}
              {...register('correo')}
            />
          </FormField>
          <FormField label="Celular" htmlFor="advisor-celular" required error={errors.celular?.message}>
            <Input
              id="advisor-celular"
              error={Boolean(errors.celular)}
              {...register('celular')}
            />
          </FormField>
          <FormField label="Proyectos" error={errors.proyectoIds?.message}>
            <Controller
              control={control}
              name="proyectoIds"
              render={({ field }) => (
                <div className="space-y-2">
                  {proyectos.length === 0 && (
                    <p className="text-sm text-muted-foreground">No hay proyectos disponibles.</p>
                  )}
                  {proyectos.map((proyecto) => {
                    const checked = field.value.includes(proyecto.id);
                    return (
                      <label key={proyecto.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => {
                            field.onChange(
                              value
                                ? [...field.value, proyecto.id]
                                : field.value.filter((id) => id !== proyecto.id),
                            );
                          }}
                        />
                        <span>{proyecto.nombre}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            />
          </FormField>
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
